// Gameplay sound effects. Two flavours:
//  - one-shots (jump, strafe, trivia, found, timeout): a fresh Audio()
//    instance per play() call, so two overlapping plays (e.g. mashing the
//    jump key) don't cut each other off - they're short and cheap enough
//    that this is simpler than pooling.
//  - loops (footsteps, wind): one persistent Audio element each, started
//    and stopped rather than recreated, since restarting a loop from
//    scratch every toggle would pop/click and lose the loop's position.
import { isMuted, onMuteChange } from "./muteState.js";

// Opus first (smaller, same/better quality) with an mp3 fallback for
// browsers that can't decode it (Safari) - same trade-off audio.js makes
// for the music track, applied here to the wav-sourced one-shots/loop.
// zoomOut/placeMarker/wind were authored straight to mp3 and have no opus
// counterpart, so they're left as plain paths below.
const canPlayOpus = document.createElement("audio").canPlayType('audio/ogg; codecs="opus"') !== "";
const src = (base) => `${base}.${canPlayOpus ? "opus" : "mp3"}`;

// Roughly balanced against the backing track (music sits at 0.4, see
// audio.js): wind is a barely-there texture, footsteps read as present but
// not distracting, and one-shots are loud enough to land as clear feedback
// over both without spiking above the "found" payoff, which stays the
// loudest single cue in the game.
const ONE_SHOTS = {
  jump: { src: src("/sfx/jump"), volume: 0.5 },
  strafe: { src: src("/sfx/strafe"), volume: 0.45, rate: 1.2 }, // slightly sped up so it reads as a quick dash, not a second jump
  triviaOpen: { src: src("/sfx/triva_opening"), volume: 0.55 },
  triviaCorrect: { src: src("/sfx/triva_correct"), volume: 0.65 },
  triviaWrong: { src: src("/sfx/triva_wrong"), volume: 0.55 },
  found: { src: src("/sfx/hider_found"), volume: 0.8 },
  timeout: { src: src("/sfx/timer_ran_out"), volume: 0.7 },
  zoomOut: { src: "/sfx/zoom_out.mp3", volume: 0.5 },
  placeMarker: { src: "/sfx/place_marker.mp3", volume: 0.55 },
  drink: { src: src("/sfx/drink"), volume: 0.35 },
};

const FOOTSTEPS_VOLUME = 0.4;
const WIND_VOLUME = 0.16;

export function createSfxController() {
  const muted = { value: isMuted() };
  const loopAudios = [];

  function createLoop(src, volume) {
    const audio = new Audio(src);
    audio.loop = true;
    audio.volume = volume;
    audio.preload = "auto";
    audio.muted = muted.value;
    loopAudios.push(audio);
    return audio;
  }

  const footsteps = createLoop(src("/sfx/footsteps"), FOOTSTEPS_VOLUME);
  const wind = createLoop("/sfx/wind.mp3", WIND_VOLUME);

  onMuteChange((next) => {
    muted.value = next;
    loopAudios.forEach((audio) => { audio.muted = next; });
  });

  // Wind is ambient scene texture, not tied to gameplay state - it starts
  // trying to play immediately and keeps looping for as long as the page
  // is open, same autoplay-gesture dance as the music track.
  function tryPlayWind() {
    wind.play().catch(() => {});
  }
  tryPlayWind();

  let footstepsPlaying = false;
  function setFootstepsPlaying(playing) {
    if (playing === footstepsPlaying) return;
    footstepsPlaying = playing;
    if (playing) footsteps.play().catch(() => {});
    else footsteps.pause();
  }

  // Tab switched away / phone locked - pause the ambient loops rather than
  // let them keep running unheard, and pick back up on return (honoring
  // whatever footstepsPlaying/wind state is current at that point, not
  // necessarily what it was when we paused).
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      footsteps.pause();
      wind.pause();
    } else {
      if (footstepsPlaying) footsteps.play().catch(() => {});
      tryPlayWind();
    }
  });

  function playOneShot(name) {
    const def = ONE_SHOTS[name];
    if (!def) return;
    const audio = new Audio(def.src);
    audio.volume = def.volume;
    audio.muted = muted.value;
    if (def.rate) audio.playbackRate = def.rate;
    audio.play().catch(() => {});
  }

  return {
    setFootstepsPlaying,
    playJump: () => playOneShot("jump"),
    playStrafe: () => playOneShot("strafe"),
    playTriviaOpen: () => playOneShot("triviaOpen"),
    playTriviaCorrect: () => playOneShot("triviaCorrect"),
    playTriviaWrong: () => playOneShot("triviaWrong"),
    playFound: () => playOneShot("found"),
    playTimeout: () => playOneShot("timeout"),
    playZoomOut: () => playOneShot("zoomOut"),
    playPlaceMarker: () => playOneShot("placeMarker"),
    playDrink: () => playOneShot("drink"),
    // Autoplay may still be blocked until a user gesture - PLAY counts as
    // one, same as the music controller's retryAutoplay().
    retryWindAutoplay: tryPlayWind,
  };
}
